import { Component, Inject, DOCUMENT, EventEmitter, Input, OnChanges, Output, SimpleChanges, OnInit, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { TranslateService } from "@ngx-translate/core";
import { takeUntil } from "rxjs/operators";

export interface BreadcrumbItem {
  label: string;
  routerLink?: string[];
  clickHandler?: () => void;
}

/** Matches the opacity transition on `.fui-hero-media img` in `_fender-ui.scss`. */
const HERO_FADE_MS = 600;

interface HeroLayer {
  id: number;
  url: string;
  shown: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-layout-header",
  templateUrl: "layout-header.component.html",
  standalone: false,
})
export class FuiLayoutHeaderComponent extends BaseComponent implements OnChanges, OnInit, OnDestroy {
  @Input() breadcrumbs: BreadcrumbItem[] = [];
  @Input() headingText: string;
  @Input() description: string;
  @Input() showDescription: boolean = true;
  /**
   * Render skeleton loaders in place of the heading + description while the page's own
   * config is loading. Breadcrumbs still render (they're driven by the route, so they're
   * available immediately). Reusable — any layout-header consumer can pass `[loading]="true"`
   * while it's waiting on its own config to resolve.
   */
  @Input() loading: boolean = false;

  /**
   * Optional hero image URL. When set, the header renders as a full-bleed hero region
   * (`.fui-hero-section`) with the image as its background — the same treatment the
   * community-detail page uses via `community.branding.hero_image_url`, now generalized
   * so any page can adopt it. Pair with `heroTheme` when text needs to sit against the
   * image at a specific contrast.
   */
  @Input() heroImageUrl?: string | null;
  /**
   * `"light"` = dark text on the image (use when the image is a light photo);
   * `"dark"` = light text (use when the image is dark). Applied as
   * `theme_plainlight` / `theme_plaindark` on the header so every themed rule (text
   * color, breadcrumb link color, nav-pill color) resolves against the image, not the
   * viewer's global body theme. Only honored when `heroImageUrl` is also set.
   */
  @Input() heroTheme?: "light" | "dark" | null;
  /**
   * Whether to render the two-layer gradient scrim that fades the hero image into the
   * page background at the bottom edge. Default `true` — every mockup we've built with
   * a hero has wanted this. Set `false` to disable when the image already bleeds cleanly
   * (or when you specifically want a hard edge). Only honored when `heroImageUrl` is set.
   */
  @Input() includeGradient: boolean = true;
  /**
   * Whether to run the slow Ken Burns "drift" animation on the hero image on load — a
   * 42s alternating scale + subtle pan. Default `true`. `prefers-reduced-motion: reduce`
   * viewers still get a static, slightly-zoomed frame regardless of this setting. Only
   * honored when `heroImageUrl` is set.
   */
  @Input() includeKenBurnsEffect: boolean = true;
  /**
   * A back arrow beside the heading, as on product detail, for pages one step down from
   * another (the Communities hub's explorers). The page decides where back goes — usually
   * `HelperService.goBack()`, which also makes the page it returns to slide in from the left.
   */
  @Input() showBackButton: boolean = false;
  @Output() backClicked = new EventEmitter<void>();

  public translatedHeadingText: string;
  public translatedDescription: string;
  public translatedBreadcrumbs: BreadcrumbItem[] = [];

  /**
   * Tracks whether THIS instance owns the hero-nav mode state — `body.ghost-nav-route`,
   * `appService.showGhostNav`, and the scroll listener. Set on init/@Input change when
   * `heroImageUrl` is truthy; cleared on destroy or when the input flips falsy. Guards
   * cleanup so a header that never opted in doesn't strip state off a page that
   * manages it itself.
   */
  private _ownedGhostNavRoute = false;

  /**
   * The hero images on screen. Normally one; while `heroImageUrl` changes, the outgoing one
   * stays underneath until the incoming one has LOADED and faded in over it, then is dropped —
   * so a swap (the Communities hub switching community) crossfades instead of flashing the
   * page background or popping in half-decoded.
   */
  public heroLayers: HeroLayer[] = [];
  private _heroLayerId = 0;
  private _heroLayerTimer: any = null;

  /**
   * Scroll-based nav-transparency listener. `appService.showGhostNav = true` renders
   * the fixed top nav transparent (so the hero image bleeds behind it); flipping to
   * `false` past a small scroll threshold restores the nav's solid background so the
   * scrolled-up hero text doesn't overlap with header controls. Matches the pattern
   * `<myp-community>` used before this was hoisted into the layout-header (2026-09-01).
   */
  private _heroScrollHandler = (e: Event) => {
    // Only the PAGE's own scroll decides this. The listener is registered on the document in
    // the capture phase, and although scroll events do not bubble, a capture-phase document
    // listener still receives them from every scrollable descendant — so reading
    // `e.target.scrollTop` let any inner column speak for the page. The community layout has
    // two such columns (the channels rail and the post pane, both `overflow-y: auto` and
    // sticky), and scrolling either to ITS top reported 0 and brought the hero blur back
    // while the page was still scrolled well down.
    const target: any = e.target;
    const isPageScroller =
      !target ||
      target === this.doc ||
      target === this.doc.documentElement ||
      target === this.doc.body ||
      (typeof target.classList?.contains === "function" && target.classList.contains("sphere-app-content"));
    if (!isPageScroller) return;
    this.appService.showGhostNav = this._readCurrentScrollTop() < 20;
  };

  constructor(
    userService: UserService,
    private translate: TranslateService,
    @Inject(DOCUMENT) private doc: Document,
    private appService: AppService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.updateTranslations();
    this.syncHeroNavMode();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateTranslations();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.headingText || changes.description || changes.breadcrumbs) {
      this.updateTranslations();
    }
    if (changes.heroImageUrl) {
      this.syncHeroNavMode();
      this.syncHeroLayers(changes.heroImageUrl.firstChange);
    }
  }

  ngOnDestroy() {
    clearTimeout(this._heroLayerTimer);
    this.teardownHeroNavMode();
    super.ngOnDestroy();
  }

  /**
   * Enable / teardown hero-nav mode based on the current `heroImageUrl`.
   *
   * Enabled state:
   *  - `body.ghost-nav-route` — zeros `.sphere-app-content` padding-top so the hero can
   *    bleed to the top of the viewport.
   *  - `appService.showGhostNav = true` — renders the fixed top nav transparent so the
   *    hero image visually shows through it.
   *  - Document scroll listener (capture phase) that flips `showGhostNav` back to
   *    `false` once the viewer scrolls past 20px, restoring the nav's solid background.
   *
   * All three are cleaned up when `heroImageUrl` clears or the component is destroyed.
   * The `_ownedGhostNavRoute` flag guards cleanup so we never rip state off a page that
   * manages it itself.
   */
  private syncHeroNavMode() {
    const shouldOwn = !!this.heroImageUrl;
    if (shouldOwn && !this._ownedGhostNavRoute) {
      this.doc.body.classList.add("ghost-nav-route");
      // `fui-hero-active` distinguishes a layout-header-rendered hero from other
      // `ghost-nav-route` sources (app.service's homepage `ghostNavRoutes` map also
      // uses `ghost-nav-route`, but those pages don't apply the light mask). The
      // nav-color override in _header.scss keys off this class to force dark text
      // when body is in light theme — homepage/other ghost-nav routes keep white text.
      this.doc.body.classList.add("fui-hero-active");
      // Set the initial nav-transparency based on the actual current scroll position of
      // the outer content container — NOT unconditionally `true`. If the viewer arrived
      // on a hero-page with the scroll container already at 500px (e.g. the previous
      // route was scrolled and its scroll persisted), starting with a transparent nav
      // means the hero title bleeds through the nav bar. Setting `showGhostNav` based
      // on actual scroll keeps the nav visible with its normal background in that case;
      // the scroll listener below then handles the "user scrolled back to top" transition
      // to transparent.
      this.appService.showGhostNav = this._readCurrentScrollTop() < 20;
      // `capture: true` — scroll events don't bubble, but they do fire during capture on
      // any ancestor. This single listener catches whichever element actually scrolls on
      // the page (document / `#contentContainer` / `.sphere-app-content`) without us
      // needing to know which.
      this.doc.addEventListener("scroll", this._heroScrollHandler, { capture: true, passive: true } as any);
      this._ownedGhostNavRoute = true;
    } else if (!shouldOwn && this._ownedGhostNavRoute) {
      this.teardownHeroNavMode();
    }
  }

  /**
   * Best-effort read of the current scroll position at init time (before any scroll
   * event has fired). Checks the known scroll containers in priority order — the outer
   * `.sphere-app-content` is where the app-wide scroll lives when a route uses the
   * default layout; falls through to the document element for pages that scroll the
   * window directly.
   */
  /** Stack the new image over the current one; the first image shows without a fade. */
  private syncHeroLayers(firstChange: boolean) {
    const url = this.heroImageUrl || null;
    const top = this.heroLayers[this.heroLayers.length - 1];
    if (top?.url === url) return;
    if (!url) {
      this.heroLayers = [];
      return;
    }
    const shown = firstChange || !this.heroLayers.length;
    // Anything still fading in is abandoned; what's fully shown stays as the backdrop.
    this.heroLayers = [...this.heroLayers.filter((layer) => layer.shown), { id: ++this._heroLayerId, url, shown }];
  }

  /** The incoming image is decoded: fade it in, then drop what it covers. */
  onHeroLayerReady(layer: HeroLayer) {
    if (layer.shown || this.heroLayers[this.heroLayers.length - 1] !== layer) return;
    layer.shown = true;
    clearTimeout(this._heroLayerTimer);
    this._heroLayerTimer = setTimeout(() => (this.heroLayers = this.heroLayers.filter((l) => l === layer || l.id > layer.id)), HERO_FADE_MS + 50);
  }

  private _readCurrentScrollTop(): number {
    const appContent = this.doc.querySelector<HTMLElement>(".sphere-app-content");
    if (appContent && appContent.scrollTop > 0) return appContent.scrollTop;
    return this.doc.documentElement?.scrollTop || 0;
  }

  private teardownHeroNavMode() {
    if (!this._ownedGhostNavRoute) return;
    this.doc.body.classList.remove("ghost-nav-route");
    this.doc.body.classList.remove("fui-hero-active");
    this.appService.showGhostNav = false;
    this.doc.removeEventListener("scroll", this._heroScrollHandler, { capture: true } as any);
    this._ownedGhostNavRoute = false;
  }

  private translateString(keyOrText: string): string {
    if (!keyOrText) {
      return keyOrText;
    }

    const translated = this.translate.instant(keyOrText);
    return translated !== keyOrText ? translated : keyOrText;
  }

  private updateTranslations() {
    this.translatedHeadingText = this.translateString(this.headingText);
    this.translatedDescription = this.translateString(this.description);
    this.translatedBreadcrumbs = this.breadcrumbs.map((crumb) => ({
      ...crumb,
      label: this.translateString(crumb.label),
    }));
  }
}
